import { SafetyCertificateOutlined } from '@ant-design/icons';
import { CallRevokeBiometricLinksRequest } from '@gooduncles/gu-app-schema';
import { Alert, Button, Checkbox, Form, Input, Modal, notification } from 'antd';
import { FC, useState } from 'react';

import { useAuth } from 'src/stores/auth-context';

import { parseIfObject } from 'src/utils/common';
import { callRevokeBiometricLinks } from 'src/utils/firebase/firebase-callable';
import { AuditLogger } from 'src/utils/audit-logger';

const auditLogger = AuditLogger.getInstance();

const MIN_REASON_LENGTH = 5;

const ACKNOWLEDGEMENTS = [
  '대상 사용자의 모든 생체인증(지문/Face ID) 기기 연동이 회수됩니다.',
  '회수 후 해당 기기들은 생체인증으로 로그인할 수 없어 재연동이 필요합니다.',
  '되돌릴 수 없으며, 분실·탈취 등 본인 확인을 마친 뒤에만 사용하세요.',
] as const;

interface FormValues {
  reason: string;
}

interface RevokeBiometricLinksButtonProps {
  userId: string;
  userLabel?: string;
  /** 감사 로그/Slack 식별용. 매장 정보 없으면 생략. */
  storeName?: string | null;
  storeCode?: string | null;
}

const RevokeBiometricLinksButton: FC<RevokeBiometricLinksButtonProps> = ({
  userId,
  userLabel,
  storeName,
  storeCode,
}) => {
  const { user: operator } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState<boolean[]>(() => ACKNOWLEDGEMENTS.map(() => false));
  const [form] = Form.useForm<FormValues>();

  const canUse = operator?.role === 'admin' || operator?.role === 'manager';
  if (!canUse) {
    return null;
  }

  const allAcknowledged = acknowledged.every(Boolean);

  const closeModal = () => {
    setOpen(false);
    setLoading(false);
    setAcknowledged(ACKNOWLEDGEMENTS.map(() => false));
    form.resetFields();
  };

  const toggleAck = (index: number, checked: boolean) => {
    if (loading) return;
    setAcknowledged((prev) => prev.map((value, i) => (i === index ? checked : value)));
  };

  const storeSuffix = (() => {
    const parts: string[] = [];
    if (storeName) parts.push(`매장명=${storeName}`);
    if (storeCode) parts.push(`매장코드=${storeCode}`);
    return parts.length ? `, ${parts.join(', ')}` : '';
  })();

  const runRevoke = async (values: FormValues) => {
    const reason = values.reason.trim();
    setLoading(true);

    try {
      const payload: CallRevokeBiometricLinksRequest = {
        mode: 'byUid',
        uid: userId,
        reason,
      };
      const { data } = await callRevokeBiometricLinks(payload);

      if (data.result === 'success') {
        const deletedCount = data.deletedCount ?? 0;
        notification.success({
          message: deletedCount > 0 ? '생체인증 연동 회수 완료' : '회수할 연동 없음 (no-op)',
          description: `${userLabel ?? userId}\n회수된 연동: ${deletedCount}건`,
          style: { whiteSpace: 'pre-line' },
        });
        auditLogger.log({
          action: 'DELETE',
          entity: 'user',
          entityId: userId,
          summary: `관리자 생체인증 연동 회수${deletedCount > 0 ? '' : '/no-op'} - ${
            userLabel ?? userId
          }, deleted=${deletedCount}${storeSuffix}, reason=${reason}`,
          metadata: {
            deletedCount,
            reason,
            storeName: storeName ?? null,
            storeCode: storeCode ?? null,
          },
        });
        closeModal();
        return;
      }

      const reasonText = data.reason ?? '알 수 없는 에러가 발생했습니다.';
      notification.error({
        message: `생체인증 연동 회수 실패 (${data.errorCode ?? 'UNKNOWN'})`,
        description: reasonText,
      });
      auditLogger.log({
        action: 'DELETE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 생체인증 연동 회수 실패(${data.errorCode ?? 'UNKNOWN'})${storeSuffix} - ${reasonText}`,
        level: 'error',
      });
    } catch (error) {
      console.error(error);
      const reasonText = parseIfObject(error);
      notification.error({
        message: '생체인증 연동 회수 실패',
        description: reasonText,
      });
      auditLogger.log({
        action: 'DELETE',
        entity: 'user',
        entityId: userId,
        summary: `관리자 생체인증 연동 회수 실패${storeSuffix} - ${reasonText}`,
        level: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    Modal.confirm({
      title: '생체인증 연동을 회수하시겠습니까?',
      content: `대상 계정(${userLabel ?? userId})에 연결된 모든 생체인증 기기 연동이 즉시 회수되며 되돌릴 수 없습니다.`,
      okText: '회수 진행',
      okType: 'danger',
      cancelText: '취소',
      onOk: () => runRevoke(values),
    });
  };

  return (
    <>
      <Button size='small' type='link' danger icon={<SafetyCertificateOutlined />} onClick={() => setOpen(true)}>
        생체인증 회수
      </Button>
      <Modal
        open={open}
        title='관리자 생체인증 연동 회수'
        onCancel={closeModal}
        maskClosable={!loading}
        closable={!loading}
        footer={[
          <Button key='cancel' onClick={closeModal} disabled={loading}>
            취소
          </Button>,
          <Button key='submit' type='primary' danger loading={loading} disabled={!allAcknowledged} onClick={onSubmit}>
            회수
          </Button>,
        ]}
      >
        <Alert
          type='error'
          showIcon
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
          message='대상 계정의 모든 생체인증 연동을 회수합니다'
          description={`대상 계정: ${userLabel ?? userId}\nuid: ${userId}\n탈취·분실된 기기의 생체인증을 무력화하는 kill switch입니다.`}
        />

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>아래 항목을 모두 숙지했는지 확인하세요</div>
          {ACKNOWLEDGEMENTS.map((text, index) => (
            <div key={text} style={{ marginBottom: 4 }}>
              <Checkbox
                checked={acknowledged[index]}
                disabled={loading}
                onChange={(e) => toggleAck(index, e.target.checked)}
              >
                {text}
              </Checkbox>
            </div>
          ))}
        </div>

        <Form form={form} layout='vertical' disabled={loading || !allAcknowledged}>
          <Form.Item
            label='CS 사유 (감사 로그에 기록)'
            name='reason'
            rules={[
              { required: true, message: '회수 사유를 입력하세요.' },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length < MIN_REASON_LENGTH) {
                    return Promise.reject(new Error(`공백 제외 ${MIN_REASON_LENGTH}자 이상 입력하세요.`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea rows={2} placeholder='예: 단말 분실로 생체인증 탈취 우려, 매장 전화로 본인 확인 완료' />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RevokeBiometricLinksButton;
